import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import MessageModal from '../Common/MessageModal';
import {
  Container,
  LeagueViewContainer,
  PageHeader,
  PageSubtitle,
  FormSection,
  FormSectionLeague,
  FormTitle,
  FormGrid,
  FormGroup,
  Label,
  Input,
  Select,
  SubmitButton,
  SecondaryButton,
  TelemarkToggle,
  ToggleButton,
  LeagueCard,
  LeagueHeader,
  LeagueTitle,
  LeagueInfo,
  CompetitionCard,
  CompetitionHeader,
  CompetitionTitle,
  StandingsTable,
  TableHeader,
  TableCell,
  TableRow,
  ErrorMessage,
  SuccessMessage,
  LoadingMessage,
  EmptyMessage,
  AthleteCheckbox,
  AthleteGrid,
  InputWithIcon,
  RandomIconButton,
} from './styles';
import { INPUT_EMPTY_PLACEHOLDER_COLOR } from '../TrainingSessions/styles';
import { PageTitle } from '../CoachDashboard/styles';
import { bananaCupAPI, athleteAPI, equipmentAPI } from '../../services/api';
import { hasPlanOrHigher } from '../../utils/subscriptionUtils';
import { Athlete as AthleteType } from '../../types/mongodb';

interface Athlete {
  _id: string;
  firstName: string;
  lastName: string;
}

interface Equipment {
  _id: string;
  name: string;
  type: 'suit' | 'skis' | 'shoes';
  athleteId?: string | { _id: string; firstName: string; lastName: string };
}

interface BananaCupJump {
  _id: string;
  athleteId: string | Athlete;
  firstRoundStartingGate?: number;
  firstRoundDistance?: number;
  firstRoundWind?: string;
  firstRoundSpeed?: number;
  firstRoundTelemark?: boolean;
  firstRoundRating?: number;
  secondRoundStartingGate?: number;
  secondRoundDistance?: number;
  secondRoundWind?: string;
  secondRoundSpeed?: number;
  secondRoundTelemark?: boolean;
  secondRoundRating?: number;
  thirdRoundStartingGate?: number;
  thirdRoundDistance?: number;
  thirdRoundWind?: string;
  thirdRoundSpeed?: number;
  thirdRoundTelemark?: boolean;
  thirdRoundRating?: number;
  fourthRoundStartingGate?: number;
  fourthRoundDistance?: number;
  fourthRoundWind?: string;
  fourthRoundSpeed?: number;
  fourthRoundTelemark?: boolean;
  fourthRoundRating?: number;
  fifthRoundStartingGate?: number;
  fifthRoundDistance?: number;
  fifthRoundWind?: string;
  fifthRoundSpeed?: number;
  fifthRoundTelemark?: boolean;
  fifthRoundRating?: number;
  totalDistance?: number;
  position?: number;
  points?: number;
  suitId: string | Equipment;
  skisId: string | Equipment;
  shoesId?: string | Equipment;
  bindingsId?: string | Equipment;
  hillSize?: string;
}

interface Competition {
  _id: string;
  competitionNumber: number;
  date: string;
  startTime?: string;
  endTime?: string;
  location: string;
  weather: string;
  hillSize: string;
  jumps: BananaCupJump[];
  isCompleted: boolean;
}

interface BananaCup {
  _id: string;
  name: string;
  numberOfCompetitions: number;
  athleteIds: (string | Athlete)[];
  punishment?: string;
  competitions: Competition[];
}

interface BananaCupProps {
  athleteId?: string; // Optional prop to indicate jumper mode (hide create button)
}

const BananaCup: React.FC<BananaCupProps> = ({ athleteId: filterAthleteId }) => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [bananaCups, setBananaCups] = useState<BananaCup[]>([]);
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showJumpSuccessModal, setShowJumpSuccessModal] = useState(false);
  const [jumpSuccessMessage, setJumpSuccessMessage] = useState<string>('');
  
  // New league form state
  const [showNewLeagueForm, setShowNewLeagueForm] = useState(false);
  const [newLeagueData, setNewLeagueData] = useState({
    name: '',
    numberOfCompetitions: 1,
    athleteIds: [] as string[],
    punishment: '',
  });

  // Punishment options
  const punishments = [
    'Loser has to buy bananas for all competitors',
    'Loser wax skis on next training for all competitors',
    'Loser has to run on top of the hill',
    'Loser has to wash club vans',
    'Loser has to sing national anthem in front of others',
    'Loser has to do 30 push ups',
    'Loser has to walk on top of the hill on the next training',
  ];

  const handleRandomPunishment = () => {
    const randomPunishment = punishments[Math.floor(Math.random() * punishments.length)];
    setNewLeagueData({ ...newLeagueData, punishment: randomPunishment });
  };
  
  // Selected league and competition
  const [selectedLeague, setSelectedLeague] = useState<BananaCup | null>(null);
  const [selectedCompetition, setSelectedCompetition] = useState<number | null>(null);
  const [expandedCompetition, setExpandedCompetition] = useState<number | null>(null);
  const [competitionSessionActive, setCompetitionSessionActive] = useState<number | null>(null);
  
  // Rename state
  const [isRenaming, setIsRenaming] = useState(false);
  const [newCupName, setNewCupName] = useState('');
  
  // Jump form state - now for individual rounds
  const [jumpFormData, setJumpFormData] = useState({
    athleteId: '',
    roundNumber: 1, // 1, 2, 3, etc.
    suitId: '',
    skisId: '',
    shoesId: '',
    bindingsId: '',
    startingGate: 0,
    distance: 0,
    wind: '',
    speed: 0,
    rating: 0,
    telemark: false,
  });

  // Raw input values for decimal separator handling (comma/period)
  const [rawInputValues, setRawInputValues] = useState({
    distance: '0',
    speed: '0',
    rating: '0',
  });
  
  // Competition details form state
  const [competitionDetails, setCompetitionDetails] = useState({
    date: '',
    startTime: '',
    endTime: '',
    location: '',
    weather: '',
    hillSize: '',
  });
  
  // Selected athletes for the current competition
  const [selectedAthletesForCompetition, setSelectedAthletesForCompetition] = useState<string[]>([]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const state = location.state as {
      bananaCupId?: string;
      bananaCupCompetitionId?: string;
      bananaCupCompetitionNumber?: number;
      openNewLeagueForm?: boolean;
    } | null;
    if (!state) return;
    if (state.openNewLeagueForm) {
      setShowNewLeagueForm(true);
      window.history.replaceState({}, document.title);
      return;
    }
    if (!state.bananaCupId) return;
    const hasCup = bananaCups.some(cup => cup._id === state.bananaCupId);
    if (!hasCup) return;
    handleSelectLeague(state.bananaCupId);
    window.history.replaceState({}, document.title);
  }, [bananaCups, location.state]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [cupsData, athletesData, equipmentData] = await Promise.all([
        bananaCupAPI.getAll(),
        athleteAPI.getAll(),
        equipmentAPI.getAll(),
      ]);
      setBananaCups(cupsData);
      // Show all athletes; lock selection by plan in UI
      setAthletes(athletesData);
      setEquipment(equipmentData);
      setError(null);
    } catch (err) {
      setError('Failed to load data. Please try again.');
      console.error('Error fetching data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateLeague = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newLeagueData.athleteIds.length === 0) {
      setError('Please select at least one athlete');
      return;
    }
    
    try {
      console.log('Creating league with data:', newLeagueData);
      const newLeague = await bananaCupAPI.create(newLeagueData);
      setBananaCups([newLeague, ...bananaCups]);
      setShowNewLeagueForm(false);
      setNewLeagueData({ name: '', numberOfCompetitions: 1, athleteIds: [], punishment: '' });
      setSuccess('League created successfully!');
      setTimeout(() => setSuccess(null), 3000);
      setError(null);
    } catch (err: any) {
      const errorMessage = err?.message || err?.response?.data?.message || 'Failed to create league. Please try again.';
      setError(errorMessage);
      console.error('Error creating league:', err);
    }
  };

  const handleSelectLeague = async (leagueId: string) => {
    try {
      const league = await bananaCupAPI.getById(leagueId);
      setSelectedLeague(league);
      setSelectedCompetition(null);
    } catch (err) {
      setError('Failed to load league details.');
      console.error('Error loading league:', err);
    }
  };

  const handleSelectCompetition = (competitionNumber: number) => {
    // Only allow selection if competition session is active or competition is completed
    const competition = selectedLeague?.competitions.find(
      c => c.competitionNumber === competitionNumber
    );
    if (competition && (competitionSessionActive === competitionNumber || competition.isCompleted)) {
      setSelectedCompetition(competitionNumber);
      if (competition && selectedLeague) {
        setCompetitionDetails({
          date: competition.date || '',
          startTime: competition.startTime || '',
          endTime: competition.endTime || '',
          location: competition.location || '',
          weather: competition.weather || '',
          hillSize: competition.hillSize || '',
        });
        // Initialize selected athletes with all league athletes
        const allAthleteIds = selectedLeague.athleteIds.map(athleteId => 
          typeof athleteId === 'object' ? athleteId._id : athleteId
        );
        setSelectedAthletesForCompetition(allAthleteIds);
        // Reset round number when selecting competition
        setJumpFormData(prev => ({
          ...prev,
          roundNumber: 1, // Reset to round 1 when selecting competition
        }));
        setRawInputValues({
          distance: '0',
          speed: '0',
          rating: '0',
        });
      }
    }
  };

  const handleUpdateCompetitionDetails = async () => {
    if (!selectedLeague || !selectedCompetition) return;
    
    try {
      const updated = await bananaCupAPI.updateCompetition(
        selectedLeague._id,
        selectedCompetition,
        competitionDetails
      );
      setSelectedLeague(updated);
      setSuccess('Competition details updated!');
      setTimeout(() => setSuccess(null), 3000);
      setError(null);
    } catch (err) {
      setError('Failed to update competition details.');
      console.error('Error updating competition:', err);
    }
  };

  const handleEditCompetition = (competitionNumber: number) => {
    if (!selectedLeague) return;
    const competition = selectedLeague.competitions.find(c => c.competitionNumber === competitionNumber);
    if (!competition) return;
    
    setSelectedCompetition(competitionNumber);
    setCompetitionDetails({
      date: competition.date || '',
      startTime: competition.startTime || '',
      endTime: competition.endTime || '',
      location: competition.location || '',
      weather: competition.weather || '',
      hillSize: competition.hillSize || '',
    });
    setExpandedCompetition(competitionNumber);
    // Scroll to competition details form
    setTimeout(() => {
      const formElement = document.querySelector('[data-competition-details-form]');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleDeleteCompetition = async (competitionNumber: number) => {
    if (!selectedLeague) return;
    
    if (!window.confirm(`Are you sure you want to delete Competition ${competitionNumber}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      // Note: This assumes the API supports deleting competitions
      // If not, we may need to add this endpoint
      const updated = await bananaCupAPI.update(selectedLeague._id, {
        competitions: selectedLeague.competitions.filter(c => c.competitionNumber !== competitionNumber)
      });
      setSelectedLeague(updated);
      setSuccess(`Competition ${competitionNumber} deleted!`);
      setTimeout(() => setSuccess(null), 3000);
      setError(null);
      if (selectedCompetition === competitionNumber) {
        setSelectedCompetition(null);
      }
    } catch (err) {
      setError('Failed to delete competition. Please try again.');
      console.error('Error deleting competition:', err);
    }
  };

  const handleStartRename = () => {
    if (!selectedLeague) return;
    setNewCupName(selectedLeague.name);
    setIsRenaming(true);
  };

  const handleCancelRename = () => {
    setIsRenaming(false);
    setNewCupName('');
  };

  const handleSaveRename = async () => {
    if (!selectedLeague || !newCupName.trim()) return;
    
    try {
      const updated = await bananaCupAPI.update(selectedLeague._id, { name: newCupName.trim() });
      setSelectedLeague(updated);
      setIsRenaming(false);
      setNewCupName('');
      setSuccess('Banana Cup name updated!');
      setTimeout(() => setSuccess(null), 3000);
      setError(null);
    } catch (err) {
      setError('Failed to update Banana Cup name.');
      console.error('Error updating Banana Cup name:', err);
    }
  };

  const handleAddJump = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLeague || !competitionSessionActive) {
      setError('Please start the competition session first (fill in details and click "START COMPETITION SESSION").');
      return;
    }

    // Client-side validation so user sees clear messages (HTML5 validation may not show on all devices)
    if (!jumpFormData.athleteId || jumpFormData.athleteId.trim() === '') {
      setError('Please select an athlete.');
      return;
    }
    if (!jumpFormData.suitId || jumpFormData.suitId.trim() === '') {
      setError('Please select a suit.');
      return;
    }
    if (!jumpFormData.skisId || jumpFormData.skisId.trim() === '') {
      setError('Please select skis.');
      return;
    }
    setError(null);

    try {
      // Prepare jump data based on round number
      const jumpData: any = {
        athleteId: jumpFormData.athleteId,
        roundNumber: jumpFormData.roundNumber,
        suitId: jumpFormData.suitId,
        skisId: jumpFormData.skisId,
        shoesId: jumpFormData.shoesId,
        bindingsId: jumpFormData.bindingsId,
        hillSize: competitionDetails.hillSize,
        startingGate: jumpFormData.startingGate,
        distance: jumpFormData.distance,
        wind: jumpFormData.wind,
        speed: jumpFormData.speed,
        rating: jumpFormData.rating,
        telemark: jumpFormData.telemark,
      };

      const updated = await bananaCupAPI.addJump(
        selectedLeague._id,
        competitionSessionActive,
        jumpData
      );
      setSelectedLeague(updated);
      
      // Reset form but keep athlete and round number if adding another round
      setJumpFormData({
        athleteId: jumpFormData.athleteId, // Keep athlete selected
        roundNumber: jumpFormData.roundNumber, // Keep round number
        suitId: jumpFormData.suitId, // Keep equipment
        skisId: jumpFormData.skisId,
        shoesId: jumpFormData.shoesId,
        bindingsId: jumpFormData.bindingsId,
        startingGate: 0,
        distance: 0,
        wind: '',
        speed: 0,
        rating: 0,
        telemark: false,
      });
      setRawInputValues({
        distance: '0',
        speed: '0',
        rating: '0',
      });
      setJumpSuccessMessage(`Round ${jumpFormData.roundNumber} added successfully!`);
      setShowJumpSuccessModal(true);
      setError(null);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to add jump. Please try again.';
      setError(message);
      console.error('Error adding jump:', err);
    }
  };

  const handleFinalizeCompetition = async () => {
    if (!selectedLeague || !selectedCompetition) return;
    
    if (!window.confirm('Are you sure you want to finalize this competition? This will calculate results and assign points.')) {
      return;
    }
    
    try {
      const updated = await bananaCupAPI.finalizeCompetition(
        selectedLeague._id,
        selectedCompetition
      );
      setSelectedLeague(updated);
      setSuccess('Competition finalized! Results and points have been calculated.');
      setTimeout(() => setSuccess(null), 3000);
      setError(null);
    } catch (err) {
      setError('Failed to finalize competition. Please try again.');
      console.error('Error finalizing competition:', err);
    }
  };

  const getAthleteName = (athleteId: string | Athlete) => {
    if (typeof athleteId === 'object' && athleteId !== null) {
      return `${athleteId.firstName} ${athleteId.lastName}`;
    }
    const athlete = athletes.find(a => a._id === athleteId);
    return athlete ? `${athlete.firstName} ${athlete.lastName}` : 'Unknown';
  };

  const getEquipmentName = (equipmentId: string | Equipment) => {
    if (typeof equipmentId === 'object' && equipmentId !== null) {
      return equipmentId.name;
    }
    const eq = equipment.find(e => e._id === equipmentId);
    return eq ? eq.name : 'Unknown';
  };

  const getEquipmentByType = (type: 'suit' | 'skis' | 'shoes' | 'bindings', athleteId?: string) => {
    if (athleteId) {
      return equipment.filter(e => {
        const equipmentAthleteId = typeof e.athleteId === 'object' ? e.athleteId._id : e.athleteId;
        return e.type === type && equipmentAthleteId === athleteId;
      });
    }
    return equipment.filter(e => e.type === type);
  };

  const calculateOverallStandings = (league: BananaCup) => {
    const standings: Record<string, { 
      athlete: Athlete; 
      totalPoints: number;
      wins: number;
      secondPlaces: number;
      thirdPlaces: number;
    }> = {};
    
    league.athleteIds.forEach(athleteId => {
      const athlete = typeof athleteId === 'object' ? athleteId : athletes.find(a => a._id === athleteId);
      if (athlete) {
        const athleteIdStr = typeof athleteId === 'object' ? athleteId._id : athleteId;
        standings[athleteIdStr] = {
          athlete: typeof athlete === 'object' ? athlete : { _id: athleteIdStr, firstName: '', lastName: '' },
          totalPoints: 0,
          wins: 0,
          secondPlaces: 0,
          thirdPlaces: 0,
        };
      }
    });
    
    league.competitions.forEach(competition => {
      if (competition.isCompleted) {
        competition.jumps.forEach(jump => {
          const athleteIdStr = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
          if (standings[athleteIdStr] && jump.points) {
            standings[athleteIdStr].totalPoints += jump.points;
            
            // Count positions
            if (jump.position === 1) {
              standings[athleteIdStr].wins += 1;
            } else if (jump.position === 2) {
              standings[athleteIdStr].secondPlaces += 1;
            } else if (jump.position === 3) {
              standings[athleteIdStr].thirdPlaces += 1;
            }
          }
        });
      }
    });
    
    return Object.values(standings).sort((a, b) => b.totalPoints - a.totalPoints);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return as-is if invalid
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return null;
    try {
      // Time is in HH:mm format, return as-is (24-hour format)
      const [hours, minutes] = timeString.split(':');
      // Ensure proper formatting with leading zeros if needed
      const hour = hours.padStart(2, '0');
      const minute = minutes.padStart(2, '0');
      return `${hour}:${minute}`;
    } catch {
      return timeString;
    }
  };

  const formatWeather = (weather: string) => {
    if (!weather) return null;
    const weatherMap: Record<string, string> = {
      'hot': 'Hot',
      'sunny': 'Sunny',
      'moderately_cloudy': 'Moderately Cloudy',
      'cloudy': 'Cloudy',
      'windy': 'Windy',
      'drizzle': 'Drizzle',
      'light_rain': 'Light Rain',
      'heavy_rain': 'Heavy Rain'
    };
    return weatherMap[weather] || weather;
  };

  const getCompetitionInfo = (competition: Competition) => {
    const hasStarted = competition.jumps.length > 0;
    const isCompleted = competition.isCompleted;
    
    let winner = null;
    let longestDistance = null;
    let longestDistanceHolder = null;
    
    if (isCompleted && competition.jumps.length > 0) {
      // Find winner (position 1)
      const winnerJump = competition.jumps.find(j => j.position === 1);
      if (winnerJump) {
        winner = getAthleteName(winnerJump.athleteId);
      }
      
      // Find longest individual jump distance (from either first or second round)
      let maxDistance = 0;
      let holderAthleteId = null;
      
      competition.jumps.forEach(jump => {
        if (jump.firstRoundDistance && jump.firstRoundDistance > maxDistance) {
          maxDistance = jump.firstRoundDistance;
          holderAthleteId = jump.athleteId;
        }
        if (jump.secondRoundDistance && jump.secondRoundDistance > maxDistance) {
          maxDistance = jump.secondRoundDistance;
          holderAthleteId = jump.athleteId;
        }
      });
      
      if (maxDistance > 0) {
        longestDistance = maxDistance;
        longestDistanceHolder = holderAthleteId ? getAthleteName(holderAthleteId) : null;
      }
    }
    
    return { hasStarted, isCompleted, winner, longestDistance, longestDistanceHolder };
  };

  const handleStartCompetition = (competitionNumber: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (!selectedLeague) return;
    
    const competition = selectedLeague.competitions.find(
      c => c.competitionNumber === competitionNumber
    );
    
    // Don't allow starting a completed competition
    if (competition?.isCompleted) {
      return;
    }
    
    // Toggle expansion
    if (expandedCompetition === competitionNumber) {
      setExpandedCompetition(null);
    } else {
      setExpandedCompetition(competitionNumber);
      if (competition && selectedLeague) {
        setCompetitionDetails({
          date: competition.date || '',
          startTime: competition.startTime || '',
          endTime: competition.endTime || '',
          location: competition.location || '',
          weather: competition.weather || '',
          hillSize: competition.hillSize || '',
        });
        // Initialize selected athletes with all league athletes
        const allAthleteIds = selectedLeague.athleteIds.map(athleteId => 
          typeof athleteId === 'object' ? athleteId._id : athleteId
        );
        setSelectedAthletesForCompetition(allAthleteIds);
      }
    }
  };

  const handleStartCompetitionSession = async (competitionNumber: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!selectedLeague) return;
    
    const competition = selectedLeague.competitions.find(
      c => c.competitionNumber === competitionNumber
    );
    
    // Don't allow starting a completed competition
    if (competition?.isCompleted) {
      setError('This competition has already been finalized and cannot be started again.');
      return;
    }
    
    // Validate required fields
    if (!competitionDetails.date || !competitionDetails.startTime || !competitionDetails.endTime || !competitionDetails.location) {
      setError('Please fill in all required fields (Date, Start Time, End Time, Location)');
      return;
    }
    
    // Validate at least one athlete is selected
    if (selectedAthletesForCompetition.length === 0) {
      setError('Please select at least one athlete for this competition');
      return;
    }
    
    // Validate end time is after start time
    if (competitionDetails.startTime && competitionDetails.endTime) {
      const startTime = new Date(`2000-01-01T${competitionDetails.startTime}`);
      const endTime = new Date(`2000-01-01T${competitionDetails.endTime}`);
      if (endTime <= startTime) {
        setError('End time must be after start time');
        return;
      }
    }
    
    try {
      // Update competition details
      const updated = await bananaCupAPI.updateCompetition(
        selectedLeague._id,
        competitionNumber,
        {
          date: competitionDetails.date,
          startTime: competitionDetails.startTime,
          endTime: competitionDetails.endTime,
          location: competitionDetails.location,
          weather: competitionDetails.weather || undefined,
          hillSize: competitionDetails.hillSize || undefined,
        }
      );
      setSelectedLeague(updated);
      setCompetitionSessionActive(competitionNumber);
      setSelectedCompetition(competitionNumber);
      // Keep card expanded so user immediately sees the add-jump form
      setExpandedCompetition(competitionNumber);
      setError(null);
      setSuccess('Competition session started! You can now add jumps.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to start competition session.');
      console.error('Error starting competition session:', err);
    }
  };

  if (isLoading) {
    return (
      <Container>
        <PageHeader>
          <PageTitle>Banana Cup 🍌</PageTitle>
        </PageHeader>
        <LoadingMessage>Loading Banana Cup leagues...</LoadingMessage>
      </Container>
    );
  }

  if (selectedLeague) {
    const standings = calculateOverallStandings(selectedLeague);
    const currentCompetition = selectedLeague.competitions.find(
      c => c.competitionNumber === selectedCompetition
    );

    return (
      <LeagueViewContainer>
        <PageHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
            <button
              onClick={() => {
                setSelectedLeague(null);
                setSelectedCompetition(null);
                fetchData();
              }}
              style={{
                background: '#fff',
                color: 'var(--accent-orange)',
                border: 'none',
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: 'var(--shadow-sm)',
                transition: 'box-shadow 0.2s, background 0.2s',
                padding: 0,
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                e.currentTarget.style.background = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                e.currentTarget.style.background = '#fff';
              }}
              title="Back to Leagues"
            >
              <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.5 13L6 8L10.5 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {isRenaming ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                <Input
                  type="text"
                  value={newCupName}
                  onChange={(e) => setNewCupName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveRename();
                    } else if (e.key === 'Escape') {
                      handleCancelRename();
                    }
                  }}
                  style={{ minWidth: '300px', flex: 1 }}
                  autoFocus
                />
                <button
                  onClick={handleSaveRename}
                  style={{
                    background: 'var(--accent-orange)',
                    color: '#fff',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 600,
                  }}
                >
                  Save
                </button>
                <button
                  onClick={handleCancelRename}
                  style={{
                    background: '#f5f5f5',
                    color: 'var(--text-gray)',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 600,
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                <PageTitle style={{ margin: 0, flex: 1 }}>{selectedLeague.name}</PageTitle>
                {!filterAthleteId && (
                  <button
                    onClick={handleStartRename}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-gray)',
                      cursor: 'pointer',
                      padding: '0.5rem',
                      borderRadius: '0.5rem',
                      fontSize: '1.2rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f5f5f5';
                      e.currentTarget.style.color = 'var(--accent-orange)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'none';
                      e.currentTarget.style.color = 'var(--text-gray)';
                    }}
                    title="Rename league"
                  >
                    ✎
                  </button>
                )}
              </div>
            )}
          </div>
          <PageSubtitle>
            Internal league competition with {selectedLeague.numberOfCompetitions} competition(s)
          </PageSubtitle>
          {selectedLeague.punishment && (
            <div style={{
              marginTop: '0.5rem',
              padding: '0.5rem 0.75rem',
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '0.5rem',
              color: '#856404',
              fontFamily: 'Poppins, sans-serif',
              fontSize: '0.75rem',
              fontWeight: 600,
            }}>
              <strong>Punishment for Last Place:</strong> {selectedLeague.punishment}
            </div>
          )}
        </PageHeader>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        {/* Overall Standings */}
        {standings.length > 0 && (
          <FormSectionLeague>
            <FormTitle>Overall Standings</FormTitle>
            <div style={{ 
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              width: '100%',
            }}>
            <StandingsTable style={{ fontSize: isMobile ? '0.65rem' : '0.7rem', minWidth: isMobile ? '500px' : 'auto' }}>
              <thead>
                <tr>
                  <TableHeader style={{ fontSize: isMobile ? '0.6rem' : '0.7rem' }}>{isMobile ? 'POS' : 'Position'}</TableHeader>
                  <TableHeader style={{ fontSize: isMobile ? '0.6rem' : '0.7rem' }}>Athlete</TableHeader>
                  <TableHeader style={{ textAlign: 'center', paddingLeft: '0.5rem', fontSize: isMobile ? '0.6rem' : '0.7rem' }}>🥇</TableHeader>
                  <TableHeader style={{ textAlign: 'center', fontSize: isMobile ? '0.6rem' : '0.7rem' }}>🥈</TableHeader>
                  <TableHeader style={{ textAlign: 'center', paddingRight: '0.5rem', fontSize: isMobile ? '0.6rem' : '0.7rem' }}>🥉</TableHeader>
                  <TableHeader style={{ textAlign: 'center', fontSize: isMobile ? '0.6rem' : '0.7rem' }}>Total Points</TableHeader>
                </tr>
              </thead>
              <tbody>
                {standings.map((standing, index) => (
                  <TableRow key={standing.athlete._id}>
                    <TableCell style={{ fontSize: isMobile ? '0.65rem' : '0.7rem' }}>{index + 1}</TableCell>
                    <TableCell style={{
                      fontSize: isMobile ? '0.65rem' : '0.7rem',
                      ...(index === 0 ? { color: 'var(--accent-orange)', fontWeight: 700 } : {})
                    }}>
                      {typeof standing.athlete === 'object'
                        ? `${standing.athlete.firstName} ${standing.athlete.lastName}`
                        : getAthleteName(standing.athlete)}
                    </TableCell>
                    <TableCell style={{ textAlign: 'center', paddingLeft: '0.5rem', fontSize: isMobile ? '0.65rem' : '0.7rem' }}>{standing.wins}</TableCell>
                    <TableCell style={{ textAlign: 'center', fontSize: isMobile ? '0.65rem' : '0.7rem' }}>{standing.secondPlaces}</TableCell>
                    <TableCell style={{ textAlign: 'center', paddingRight: '0.5rem', fontSize: isMobile ? '0.65rem' : '0.7rem' }}>{standing.thirdPlaces}</TableCell>
                    <TableCell style={{ 
                      textAlign: 'center',
                      fontSize: isMobile ? '0.65rem' : '0.7rem',
                      ...(index === 0 ? { color: 'var(--accent-orange)', fontWeight: 700 } : {})
                    }}>
                      {standing.totalPoints}
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </StandingsTable>
            </div>
          </FormSectionLeague>
        )}

        {/* Competitions List */}
        <FormSectionLeague>
          <FormTitle>Competitions</FormTitle>
          {selectedLeague.competitions.map(competition => {
            const { hasStarted, isCompleted, winner, longestDistance, longestDistanceHolder } = getCompetitionInfo(competition);
            const isExpanded = expandedCompetition === competition.competitionNumber;
            const isSessionActive = competitionSessionActive === competition.competitionNumber;
            
            return (
              <CompetitionCard
                key={competition._id}
                $completed={isCompleted}
                style={{ cursor: isExpanded || isSessionActive ? 'default' : 'pointer' }}
              >
                <CompetitionHeader>
                  <CompetitionTitle>
                    Competition {competition.competitionNumber}
                    {isCompleted && ' ✓'}
                  </CompetitionTitle>
                  <div style={{ fontSize: '0.72rem', color: '#666' }}>
                    {competition.jumps.length} jump(s) recorded
                  </div>
                </CompetitionHeader>
                
                <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {/* Competition Details (shown when completed) */}
                  {isCompleted && (
                    <>
                      {/* Location */}
                      {competition.location && (
                        <div style={{ fontSize: '0.72rem', color: '#666' }}>
                          <strong>Location:</strong> {competition.location}
                        </div>
                      )}
                      
                      {/* Hill Size */}
                      {competition.hillSize && (
                        <div style={{ fontSize: '0.72rem', color: '#666' }}>
                          <strong>Hill Size:</strong> {competition.hillSize}
                        </div>
                      )}
                      
                      {/* Date */}
                      {competition.date && (
                        <div style={{ 
                          fontSize: '0.72rem', 
                          color: '#666',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '0.5rem'
                        }}>
                          <span>
                            <strong>Date:</strong> {formatDate(competition.date)}
                          </span>
                          {!filterAthleteId && (
                            <div style={{ 
                              display: 'flex', 
                              gap: '0.4rem',
                              alignItems: 'center',
                              marginLeft: isMobile ? 'auto' : '0.5rem'
                            }}>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleEditCompetition(competition.competitionNumber);
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                  color: '#666',
                                  padding: '0.25rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                                title="Edit competition"
                              >
                                <span style={{ fontSize: '0.65rem', color: '#333' }}>✎</span>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleDeleteCompetition(competition.competitionNumber);
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                  color: '#dc3545',
                                  padding: '0.25rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                                title="Delete competition"
                              >
                                <span style={{ fontSize: '0.65rem' }}>🗑️</span>
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Start Time */}
                      {competition.startTime && (
                        <div style={{ fontSize: '0.72rem', color: '#666' }}>
                          <strong>Start Time:</strong> {formatTime(competition.startTime)}
                        </div>
                      )}
                      
                      {/* End Time */}
                      {competition.endTime && (
                        <div style={{ fontSize: '0.72rem', color: '#666' }}>
                          <strong>End Time:</strong> {formatTime(competition.endTime)}
                        </div>
                      )}
                      
                      {/* Weather */}
                      {competition.weather && (
                        <div style={{ fontSize: '0.72rem', color: '#666' }}>
                          <strong>Weather:</strong> {formatWeather(competition.weather)}
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Winner (after competition) */}
                  {isCompleted && winner && (
                    <div style={{ fontSize: '0.72rem', color: 'var(--accent-orange)', fontWeight: 600 }}>
                      <strong>Winner:</strong> {winner}
                    </div>
                  )}
                  
                  {/* Longest Distance (after competition) */}
                  {isCompleted && longestDistance && (
                    <div style={{ fontSize: '0.72rem', color: '#666' }}>
                      <strong>Longest Distance:</strong> {longestDistance.toFixed(1)} m
                      {longestDistanceHolder && ` (${longestDistanceHolder})`}
                    </div>
                  )}
                  
                  {/* Start Competition Button - Only for coaches */}
                  {!filterAthleteId && !hasStarted && !isCompleted && !isExpanded && !isSessionActive && (
                    <SubmitButton
                      onClick={(e) => handleStartCompetition(competition.competitionNumber, e)}
                      style={{ 
                        marginTop: '0.5rem',
                        maxWidth: '200px',
                        fontSize: '0.75rem',
                        padding: '0.5rem 1rem'
                      }}
                    >
                      Start Competition
                    </SubmitButton>
                  )}
                </div>

                {/* Expanded Competition Session Form - Only for coaches */}
                {!filterAthleteId && isExpanded && !isSessionActive && !isCompleted && (
                  <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e0e6ed' }} data-competition-details-form>
                    <FormTitle style={{ fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                      {selectedCompetition === competition.competitionNumber ? 'Edit Competition Details' : 'Start new Banana Cup competition'}
                    </FormTitle>
                    <FormGrid>
                      <FormGroup>
                        <Label>Training Type</Label>
                        <Input
                          type="text"
                          value="COMPETITION"
                          disabled
                          style={{ backgroundColor: '#f5f5f5', opacity: 0.7 }}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Date</Label>
                        <Input
                          type="date"
                          value={competitionDetails.date}
                          onChange={(e) => setCompetitionDetails({ ...competitionDetails, date: e.target.value })}
                          required
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Start Time</Label>
                        <Input
                          type="time"
                          value={competitionDetails.startTime}
                          onChange={(e) => setCompetitionDetails({ ...competitionDetails, startTime: e.target.value })}
                          required
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Expected End Time</Label>
                        <Input
                          type="time"
                          value={competitionDetails.endTime}
                          onChange={(e) => setCompetitionDetails({ ...competitionDetails, endTime: e.target.value })}
                          required
                        />
                      </FormGroup>
                    </FormGrid>
                    <FormGrid>
                      <FormGroup>
                        <Label>Location</Label>
                        <Input
                          type="text"
                          value={competitionDetails.location}
                          onChange={(e) => setCompetitionDetails({ ...competitionDetails, location: e.target.value })}
                          placeholder="Enter training location"
                          required
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Hill Size</Label>
                        <Input
                          type="text"
                          value={competitionDetails.hillSize}
                          onChange={(e) => setCompetitionDetails({ ...competitionDetails, hillSize: e.target.value })}
                          placeholder="Enter hill size"
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Weather</Label>
                        <Select
                          value={competitionDetails.weather}
                          onChange={(e) => setCompetitionDetails({ ...competitionDetails, weather: e.target.value })}
                        >
                          <option value="">Select weather</option>
                          <option value="hot">HOT</option>
                          <option value="sunny">SUNNY</option>
                          <option value="moderately_cloudy">MODERATELY CLOUDY</option>
                          <option value="cloudy">CLOUDY</option>
                          <option value="windy">WINDY</option>
                          <option value="drizzle">DRIZZLE</option>
                          <option value="light_rain">LIGHT RAIN</option>
                          <option value="heavy_rain">HEAVY RAIN</option>
                        </Select>
                      </FormGroup>
                    </FormGrid>
                    <FormGroup style={{ marginTop: '1rem' }}>
                      <Label>Participating Athletes</Label>
                      <AthleteGrid>
                        {selectedLeague.athleteIds.map(athleteId => {
                          const athlete = typeof athleteId === 'object' ? athleteId : athletes.find(a => a._id === athleteId);
                          if (!athlete) return null;
                          const id = typeof athleteId === 'object' ? athleteId._id : athleteId;
                          const isSelected = selectedAthletesForCompetition.includes(id);
                          return (
                            <AthleteCheckbox key={id} selected={isSelected}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedAthletesForCompetition([...selectedAthletesForCompetition, id]);
                                  } else {
                                    setSelectedAthletesForCompetition(selectedAthletesForCompetition.filter(athleteId => athleteId !== id));
                                  }
                                }}
                                style={{ margin: 0 }}
                              />
                              {typeof athlete === 'object' ? `${athlete.firstName} ${athlete.lastName}` : getAthleteName(athleteId)}
                            </AthleteCheckbox>
                          );
                        })}
                      </AthleteGrid>
                    </FormGroup>
                    <SubmitButton
                      onClick={(e) => handleStartCompetitionSession(competition.competitionNumber, e)}
                      style={{ marginTop: '1rem' }}
                    >
                      START COMPETITION SESSION
                    </SubmitButton>
                  </div>
                )}

                {/* Jump Form (after session started) */}
                {isSessionActive && !isCompleted && (
                  <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e0e6ed' }}>
                    <FormTitle style={{ fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                      Add Competition Jump
                    </FormTitle>
                    
                    {/* Show existing jumps for selected athlete */}
                    {jumpFormData.athleteId && (() => {
                      const athleteJumps = competition.jumps.filter((jump: any) => {
                        const jumpAthleteId = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
                        return jumpAthleteId === jumpFormData.athleteId;
                      });
                      
                      if (athleteJumps.length > 0) {
                        const athleteJump = athleteJumps[0];
                        const rounds = [];
                        if (athleteJump.firstRoundDistance) rounds.push({ num: 1, dist: athleteJump.firstRoundDistance, gate: athleteJump.firstRoundStartingGate });
                        if (athleteJump.secondRoundDistance) rounds.push({ num: 2, dist: athleteJump.secondRoundDistance, gate: athleteJump.secondRoundStartingGate });
                        if (athleteJump.thirdRoundDistance) rounds.push({ num: 3, dist: athleteJump.thirdRoundDistance, gate: athleteJump.thirdRoundStartingGate });
                        if (athleteJump.fourthRoundDistance) rounds.push({ num: 4, dist: athleteJump.fourthRoundDistance, gate: athleteJump.fourthRoundStartingGate });
                        if (athleteJump.fifthRoundDistance) rounds.push({ num: 5, dist: athleteJump.fifthRoundDistance, gate: athleteJump.fifthRoundStartingGate });
                        
                        if (rounds.length > 0) {
                          return (
                            <div style={{ 
                              marginBottom: '1.5rem', 
                              padding: '1rem', 
                              background: 'rgba(0, 0, 0, 0.02)', 
                              borderRadius: '0.5rem',
                              fontSize: '0.85rem'
                            }}>
                              <div style={{ fontWeight: 700, marginBottom: '0.5rem', color: 'var(--text-gray)' }}>
                                Existing Jumps for Selected Athlete:
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem' }}>
                                {rounds.map(round => (
                                  <div key={round.num}>
                                    <strong>Round {round.num}:</strong> {round.dist}m
                                    {round.gate && ` (Gate: ${round.gate})`}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }
                      }
                      return null;
                    })()}

                    {!filterAthleteId && (
                      <form onSubmit={handleAddJump}>
                      <FormGrid>
                        <FormGroup>
                          <Label>Athlete</Label>
                          <Select
                            value={jumpFormData.athleteId}
                            onChange={(e) => {
                              setJumpFormData({ ...jumpFormData, athleteId: e.target.value });
                            }}
                            required
                            style={{
                              color: jumpFormData.athleteId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                              WebkitTextFillColor: jumpFormData.athleteId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                            }}
                          >
                            <option value="">Select Athlete</option>
                            {selectedLeague.athleteIds
                              .map(athleteId => {
                                const id = typeof athleteId === 'object' ? athleteId._id : athleteId;
                                const athlete = typeof athleteId === 'object' ? athleteId : athletes.find(a => a._id === athleteId);
                                return { id, athlete };
                              })
                              .filter(({ id }) => selectedAthletesForCompetition.includes(id))
                              .map(({ id, athlete }) => {
                                if (!athlete) return null;
                                return (
                                  <option key={id} value={id}>
                                    {typeof athlete === 'object' ? `${athlete.firstName} ${athlete.lastName}` : getAthleteName(id)}
                                  </option>
                                );
                              })}
                          </Select>
                        </FormGroup>
                        <FormGroup>
                          <Label>Round Number</Label>
                          <Select
                            value={jumpFormData.roundNumber}
                            onChange={(e) => setJumpFormData({ ...jumpFormData, roundNumber: Number(e.target.value) })}
                            required
                          >
                            <option value={1}>1st Round</option>
                            <option value={2}>2nd Round</option>
                            <option value={3}>3rd Round</option>
                            <option value={4}>4th Round</option>
                            <option value={5}>5th Round</option>
                          </Select>
                        </FormGroup>
                        <FormGroup>
                          <Label>Suit</Label>
                          <Select
                            value={jumpFormData.suitId}
                            onChange={(e) => setJumpFormData({ ...jumpFormData, suitId: e.target.value })}
                            required
                            style={{
                              color: jumpFormData.suitId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                              WebkitTextFillColor: jumpFormData.suitId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                            }}
                          >
                            <option value="">Select Suit</option>
                            {getEquipmentByType('suit', jumpFormData.athleteId).map(suit => (
                              <option key={suit._id} value={suit._id}>
                                {suit.name}
                              </option>
                            ))}
                          </Select>
                        </FormGroup>
                        <FormGroup>
                          <Label>Skis</Label>
                          <Select
                            value={jumpFormData.skisId}
                            onChange={(e) => setJumpFormData({ ...jumpFormData, skisId: e.target.value })}
                            required
                            style={{
                              color: jumpFormData.skisId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                              WebkitTextFillColor: jumpFormData.skisId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                            }}
                          >
                            <option value="">Select Skis</option>
                            {getEquipmentByType('skis', jumpFormData.athleteId).map(skis => (
                              <option key={skis._id} value={skis._id}>
                                {skis.name}
                              </option>
                            ))}
                          </Select>
                        </FormGroup>
                        <FormGroup>
                          <Label>Shoes</Label>
                          <Select
                            value={jumpFormData.shoesId}
                            onChange={(e) => setJumpFormData({ ...jumpFormData, shoesId: e.target.value })}
                            style={{
                              color: jumpFormData.shoesId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                              WebkitTextFillColor: jumpFormData.shoesId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                            }}
                          >
                            <option value="">Select Shoes (Optional)</option>
                            {getEquipmentByType('shoes', jumpFormData.athleteId).map(shoes => (
                              <option key={shoes._id} value={shoes._id}>
                                {shoes.name}
                              </option>
                            ))}
                          </Select>
                        </FormGroup>
                        <FormGroup>
                          <Label>Bindings</Label>
                          <Select
                            value={jumpFormData.bindingsId}
                            onChange={(e) => setJumpFormData({ ...jumpFormData, bindingsId: e.target.value })}
                            style={{
                              color: jumpFormData.bindingsId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                              WebkitTextFillColor: jumpFormData.bindingsId === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                            }}
                          >
                            <option value="">Select Bindings (Optional)</option>
                            {getEquipmentByType('bindings', jumpFormData.athleteId).map(bindings => (
                              <option key={bindings._id} value={bindings._id}>
                                {bindings.name}
                              </option>
                            ))}
                          </Select>
                        </FormGroup>
                      </FormGrid>
                      <FormGrid>
                        <FormGroup>
                          <Label>Starting Gate</Label>
                          <Input
                            type="number"
                            value={jumpFormData.startingGate}
                            onChange={(e) => setJumpFormData({ ...jumpFormData, startingGate: Number(e.target.value) })}
                            min="0"
                            required
                            style={{
                              color: jumpFormData.startingGate === 0 ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                              WebkitTextFillColor: jumpFormData.startingGate === 0 ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                            }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Distance (meters)</Label>
                          <Input
                            type="text"
                            value={rawInputValues.distance}
                            onChange={(e) => {
                              let value = e.target.value;
                              // Remove any non-numeric characters except comma and period
                              value = value.replace(/[^\d,.-]/g, '');
                              // Allow both comma and period as decimal separator, but only one
                              const hasComma = value.includes(',');
                              const hasPeriod = value.includes('.');
                              if (hasComma && hasPeriod) {
                                // If both exist, keep the last one
                                const lastComma = value.lastIndexOf(',');
                                const lastPeriod = value.lastIndexOf('.');
                                if (lastComma > lastPeriod) {
                                  value = value.replace(/\./g, '');
                                } else {
                                  value = value.replace(/,/g, '');
                                }
                              }
                              // Only allow numbers and one decimal separator
                              if (value === '' || /^-?\d*[,.]?\d*$/.test(value)) {
                                setRawInputValues(prev => ({ ...prev, distance: value === '' ? '0' : value }));
                                const normalizedValue = value.replace(',', '.');
                                const numValue = normalizedValue === '' || normalizedValue === '-' ? 0 : parseFloat(normalizedValue) || 0;
                                if (normalizedValue === '' || normalizedValue === '-' || (numValue >= 0 && numValue <= 999)) {
                                  setJumpFormData(prev => ({
                                    ...prev,
                                    distance: numValue
                                  }));
                                }
                              }
                            }}
                            onBlur={(e) => {
                              // On blur, ensure the value is properly formatted
                              const value = e.target.value;
                              if (value === '' || value === '-') {
                                setRawInputValues(prev => ({ ...prev, distance: '0' }));
                              } else {
                                const normalizedValue = value.replace(',', '.');
                                const numValue = parseFloat(normalizedValue) || 0;
                                setRawInputValues(prev => ({ ...prev, distance: String(numValue).replace('.', ',') }));
                              }
                            }}
                            placeholder="0"
                            required
                            style={{
                              color: ((rawInputValues.distance === '' || rawInputValues.distance === '0') && jumpFormData.distance === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                              WebkitTextFillColor: ((rawInputValues.distance === '' || rawInputValues.distance === '0') && jumpFormData.distance === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                            }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Wind</Label>
                          <Input
                            type="text"
                            value={jumpFormData.wind}
                            onChange={(e) => setJumpFormData({ ...jumpFormData, wind: e.target.value })}
                            placeholder="Enter wind conditions"
                            style={{
                              color: jumpFormData.wind === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                              WebkitTextFillColor: jumpFormData.wind === '' ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                            }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Speed (km/h)</Label>
                          <Input
                            type="text"
                            value={rawInputValues.speed}
                            onChange={(e) => {
                              let value = e.target.value;
                              // Remove any non-numeric characters except comma and period
                              value = value.replace(/[^\d,.-]/g, '');
                              // Allow both comma and period as decimal separator, but only one
                              const hasComma = value.includes(',');
                              const hasPeriod = value.includes('.');
                              if (hasComma && hasPeriod) {
                                // If both exist, keep the last one
                                const lastComma = value.lastIndexOf(',');
                                const lastPeriod = value.lastIndexOf('.');
                                if (lastComma > lastPeriod) {
                                  value = value.replace(/\./g, '');
                                } else {
                                  value = value.replace(/,/g, '');
                                }
                              }
                              // Only allow numbers and one decimal separator
                              if (value === '' || /^-?\d*[,.]?\d*$/.test(value)) {
                                setRawInputValues(prev => ({ ...prev, speed: value === '' ? '0' : value }));
                                const normalizedValue = value.replace(',', '.');
                                const numValue = normalizedValue === '' || normalizedValue === '-' ? 0 : parseFloat(normalizedValue) || 0;
                                if (normalizedValue === '' || normalizedValue === '-' || (numValue >= 0 && numValue <= 999)) {
                                  setJumpFormData(prev => ({
                                    ...prev,
                                    speed: numValue
                                  }));
                                }
                              }
                            }}
                            onBlur={(e) => {
                              // On blur, ensure the value is properly formatted
                              const value = e.target.value;
                              if (value === '' || value === '-') {
                                setRawInputValues(prev => ({ ...prev, speed: '0' }));
                              } else {
                                const normalizedValue = value.replace(',', '.');
                                const numValue = parseFloat(normalizedValue) || 0;
                                setRawInputValues(prev => ({ ...prev, speed: String(numValue).replace('.', ',') }));
                              }
                            }}
                            placeholder="0"
                            style={{
                              color: ((rawInputValues.speed === '' || rawInputValues.speed === '0') && jumpFormData.speed === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                              WebkitTextFillColor: ((rawInputValues.speed === '' || rawInputValues.speed === '0') && jumpFormData.speed === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                            }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Rating (1-10)</Label>
                          <Input
                            type="text"
                            value={rawInputValues.rating}
                            onChange={(e) => {
                              let value = e.target.value;
                              // Remove any non-numeric characters except comma and period
                              value = value.replace(/[^\d,.-]/g, '');
                              // Allow both comma and period as decimal separator, but only one
                              const hasComma = value.includes(',');
                              const hasPeriod = value.includes('.');
                              if (hasComma && hasPeriod) {
                                // If both exist, keep the last one
                                const lastComma = value.lastIndexOf(',');
                                const lastPeriod = value.lastIndexOf('.');
                                if (lastComma > lastPeriod) {
                                  value = value.replace(/\./g, '');
                                } else {
                                  value = value.replace(/,/g, '');
                                }
                              }
                              // Only allow numbers and one decimal separator
                              if (value === '' || /^-?\d*[,.]?\d*$/.test(value)) {
                                setRawInputValues(prev => ({ ...prev, rating: value === '' ? '0' : value }));
                                const normalizedValue = value.replace(',', '.');
                                const numValue = normalizedValue === '' || normalizedValue === '-' ? 0 : parseFloat(normalizedValue) || 0;
                                if (normalizedValue === '' || normalizedValue === '-' || (numValue >= 0 && numValue <= 10)) {
                                  setJumpFormData(prev => ({
                                    ...prev,
                                    rating: numValue
                                  }));
                                }
                              }
                            }}
                            onBlur={(e) => {
                              // On blur, ensure the value is properly formatted
                              const value = e.target.value;
                              if (value === '' || value === '-') {
                                setRawInputValues(prev => ({ ...prev, rating: '0' }));
                              } else {
                                const normalizedValue = value.replace(',', '.');
                                const numValue = parseFloat(normalizedValue) || 0;
                                setRawInputValues(prev => ({ ...prev, rating: String(numValue).replace('.', ',') }));
                              }
                            }}
                            placeholder="0"
                            style={{
                              color: ((rawInputValues.rating === '' || rawInputValues.rating === '0') && jumpFormData.rating === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined,
                              WebkitTextFillColor: ((rawInputValues.rating === '' || rawInputValues.rating === '0') && jumpFormData.rating === 0) ? INPUT_EMPTY_PLACEHOLDER_COLOR : undefined
                            }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Telemark Landing</Label>
                          <TelemarkToggle>
                            <ToggleButton
                              type="button"
                              active={jumpFormData.telemark}
                              onClick={() => setJumpFormData({ ...jumpFormData, telemark: true })}
                            >
                              Yes
                            </ToggleButton>
                            <ToggleButton
                              type="button"
                              active={!jumpFormData.telemark}
                              onClick={() => setJumpFormData({ ...jumpFormData, telemark: false })}
                            >
                              No
                            </ToggleButton>
                          </TelemarkToggle>
                        </FormGroup>
                      </FormGrid>
                      <SubmitButton type="submit">Add Round {jumpFormData.roundNumber}</SubmitButton>
                    </form>
                    )}
                  </div>
                )}

                {/* Competition Results (shown when competition is completed) */}
                {competition.isCompleted && competition.jumps.length > 0 && (
                  <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e0e6ed' }}>
                    <FormTitle style={{ fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                      Competition {competition.competitionNumber} Results
                    </FormTitle>
                    <div style={{ 
                      overflowX: 'auto',
                      WebkitOverflowScrolling: 'touch',
                      width: '100%',
                    }}>
                    <table style={{ 
                      width: '100%', 
                      fontSize: isMobile ? '0.55rem' : '0.6rem', 
                      borderCollapse: 'collapse',
                      minWidth: isMobile ? '700px' : 'auto'
                    }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '0.2rem', textAlign: 'left', borderBottom: '2px solid rgba(0, 0, 0, 0.1)', fontSize: isMobile ? '0.5rem' : '0.55rem' }}>{isMobile ? 'POS' : 'Position'}</th>
                          <th style={{ padding: '0.2rem', textAlign: 'left', borderBottom: '2px solid rgba(0, 0, 0, 0.1)', fontSize: isMobile ? '0.5rem' : '0.55rem' }}>Athlete</th>
                          <th style={{ padding: '0.2rem', textAlign: 'left', borderBottom: '2px solid rgba(0, 0, 0, 0.1)', fontSize: isMobile ? '0.5rem' : '0.55rem' }}>Gate</th>
                          <th style={{ padding: '0.2rem', textAlign: 'left', borderBottom: '2px solid rgba(0, 0, 0, 0.1)', fontSize: isMobile ? '0.5rem' : '0.55rem' }}>Distance</th>
                          <th style={{ padding: '0.2rem', textAlign: 'left', borderBottom: '2px solid rgba(0, 0, 0, 0.1)', fontSize: isMobile ? '0.5rem' : '0.55rem' }}>Wind</th>
                          <th style={{ padding: '0.2rem', textAlign: 'left', borderBottom: '2px solid rgba(0, 0, 0, 0.1)', fontSize: isMobile ? '0.5rem' : '0.55rem' }}>Speed</th>
                          <th style={{ padding: '0.2rem', textAlign: 'left', borderBottom: '2px solid rgba(0, 0, 0, 0.1)', fontSize: isMobile ? '0.5rem' : '0.55rem' }}>Telemark</th>
                          <th style={{ padding: '0.2rem', textAlign: 'left', borderBottom: '2px solid rgba(0, 0, 0, 0.1)', fontSize: isMobile ? '0.5rem' : '0.55rem' }}>Final Result</th>
                          <th style={{ padding: '0.2rem', textAlign: 'left', borderBottom: '2px solid rgba(0, 0, 0, 0.1)', fontSize: isMobile ? '0.5rem' : '0.55rem' }}>Points</th>
                        </tr>
                      </thead>
                      <tbody>
                        {competition.jumps
                          .sort((a, b) => (a.position || 0) - (b.position || 0))
                          .map(jump => (
                            <React.Fragment key={jump._id}>
                              {/* First row - 1st round data */}
                              <tr style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.05)' }}>
                                <td style={{ padding: '0.2rem' }} rowSpan={2}>{jump.position || '-'}</td>
                                <td style={{ padding: '0.2rem' }} rowSpan={2}>{getAthleteName(jump.athleteId)}</td>
                                <td style={{ padding: '0.2rem' }}>{jump.firstRoundStartingGate || '-'}</td>
                                <td style={{ padding: '0.2rem' }}>{jump.firstRoundDistance ? `${jump.firstRoundDistance} m` : '-'}</td>
                                <td style={{ padding: '0.2rem' }}>{jump.firstRoundWind || '-'}</td>
                                <td style={{ padding: '0.2rem' }}>{jump.firstRoundSpeed ? `${jump.firstRoundSpeed} km/h` : '-'}</td>
                                <td style={{ padding: '0.2rem' }}>{jump.firstRoundTelemark ? 'Yes' : 'No'}</td>
                                <td style={{ padding: '0.2rem' }} rowSpan={2}>{jump.totalDistance ? `${jump.totalDistance} points` : '-'}</td>
                                <td style={{ padding: '0.2rem' }} rowSpan={2}>{jump.points || '-'}</td>
                              </tr>
                              {/* Second row - 2nd round data */}
                              <tr style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.05)' }}>
                                <td style={{ padding: '0.2rem' }}>{jump.secondRoundStartingGate || '-'}</td>
                                <td style={{ padding: '0.2rem' }}>{jump.secondRoundDistance ? `${jump.secondRoundDistance} m` : '-'}</td>
                                <td style={{ padding: '0.2rem' }}>{jump.secondRoundWind || '-'}</td>
                                <td style={{ padding: '0.2rem' }}>{jump.secondRoundSpeed ? `${jump.secondRoundSpeed} km/h` : '-'}</td>
                                <td style={{ padding: '0.2rem' }}>{jump.secondRoundTelemark ? 'Yes' : 'No'}</td>
                              </tr>
                            </React.Fragment>
                          ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                )}

                {/* Finalize Button (shown in expanded card) - Only for coaches */}
                {!filterAthleteId && isSessionActive && competition.competitionNumber === competitionSessionActive && !competition.isCompleted && competition.jumps.length > 0 && (
                  <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e0e6ed' }}>
                    <SubmitButton
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!selectedLeague) return;
                        if (!window.confirm('Are you sure you want to finalize this competition? This will calculate results and assign points.')) {
                          return;
                        }
                        try {
                          const updated = await bananaCupAPI.finalizeCompetition(
                            selectedLeague._id,
                            competition.competitionNumber
                          );
                          setSelectedLeague(updated);
                          setCompetitionSessionActive(null);
                          setSuccess('Competition finalized! Results and points have been calculated.');
                          setTimeout(() => setSuccess(null), 3000);
                          setError(null);
                        } catch (err) {
                          setError('Failed to finalize competition. Please try again.');
                          console.error('Error finalizing competition:', err);
                        }
                      }}
                      style={{ background: '#dc3545' }}
                    >
                      Finalize Competition
                    </SubmitButton>
                  </div>
                )}
              </CompetitionCard>
            );
          })}
        </FormSectionLeague>
      </LeagueViewContainer>
    );
  }

  return (
    <Container>
      <MessageModal
        isOpen={showJumpSuccessModal}
        type="success"
        message={jumpSuccessMessage}
        onClose={() => setShowJumpSuccessModal(false)}
        autoCloseDelay={3000}
      />
      <PageHeader>
        <PageTitle>Banana Cup 🍌</PageTitle>
        <PageSubtitle>
          Create and manage internal competitions. Track results and standings across multiple competitions.
        </PageSubtitle>
      </PageHeader>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      {!showNewLeagueForm ? (
        <>
          {!filterAthleteId && (
            <div style={isMobile ? { display: 'flex', justifyContent: 'center', width: '100%' } : {}}>
              <SubmitButton
                onClick={() => setShowNewLeagueForm(true)}
                style={{ marginBottom: '2rem', maxWidth: '300px' }}
              >
                Start New Banana Cup
              </SubmitButton>
            </div>
          )}

          {bananaCups.length === 0 ? (
            <EmptyMessage>
              {filterAthleteId 
                ? "No Banana Cups available yet. Your coach will create Banana Cups for competitions you're participating in."
                : (
                  <>
                    No Banana Cups created yet. Create your first{' '}
                    <span
                      onClick={() => setShowNewLeagueForm(true)}
                      style={{
                        color: 'var(--accent-orange)',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontWeight: 700,
                      }}
                    >
                      Banana Cup
                    </span>{' '}
                    to get started!
                  </>
                )
              }
            </EmptyMessage>
          ) : (
            bananaCups.map(league => {
              const completedCompetitions = league.competitions.filter(c => c.isCompleted).length;
              const allCompetitionsCompleted = completedCompetitions === league.numberOfCompetitions && league.numberOfCompetitions > 0;
              const standings = calculateOverallStandings(league);
              const leader = standings.length > 0 ? standings[0] : null;
              
              return (
                <LeagueCard
                  key={league._id}
                  onClick={() => handleSelectLeague(league._id)}
                >
                  <LeagueHeader>
                    <LeagueTitle>{league.name}</LeagueTitle>
                  </LeagueHeader>
                  
                  {/* Leader/Winner Information with League Info on same line */}
                  {leader && leader.totalPoints > 0 ? (
                    <div style={{
                      marginTop: '0.75rem',
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: isMobile ? '0.75rem' : '0.9rem',
                      color: 'var(--text-gray)',
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'flex-start' : 'center', 
                        gap: isMobile ? '0.5rem' : '1rem', 
                        marginBottom: '0.25rem',
                        flexWrap: 'wrap'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <strong style={{ fontSize: isMobile ? '0.8rem' : '0.95rem' }}>
                            {allCompetitionsCompleted ? '🏆 Winner:' : 'Current Leader:'}
                          </strong>
                          <span style={{ fontWeight: 700 }}>
                            {typeof leader.athlete === 'object' 
                              ? `${leader.athlete.firstName} ${leader.athlete.lastName}`
                              : getAthleteName(leader.athlete)}
                          </span>
                        </div>
                        <LeagueInfo style={isMobile ? { 
                          flexDirection: 'column', 
                          alignItems: 'flex-start', 
                          gap: '0.25rem',
                          fontSize: isMobile ? '0.7rem' : '0.85rem'
                        } : {
                          fontSize: isMobile ? '0.7rem' : '0.85rem',
                          margin: 0
                        }}>
                          <span>{league.numberOfCompetitions} competition(s)</span>
                          <span>{completedCompetitions} completed</span>
                          <span>{league.athleteIds.length} athlete(s)</span>
                        </LeagueInfo>
                      </div>
                      <div style={{ 
                        fontSize: isMobile ? '0.7rem' : '0.85rem', 
                        opacity: 0.9,
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? '0.25rem' : '0.75rem'
                      }}>
                        <span><strong>Points:</strong> {leader.totalPoints}</span>
                        {standings.length > 1 && (
                          <span>
                            (Lead: +{leader.totalPoints - standings[1].totalPoints} pts)
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <LeagueInfo style={{
                      marginTop: '0.75rem',
                      ...(isMobile ? { 
                        flexDirection: 'column', 
                        alignItems: 'flex-start', 
                        gap: '0.25rem' 
                      } : {})
                    }}>
                      <span>{league.numberOfCompetitions} competition(s)</span>
                      <span>{completedCompetitions} completed</span>
                      <span>{league.athleteIds.length} athlete(s)</span>
                    </LeagueInfo>
                  )}
                  
                  {/* Show if no competitions completed yet */}
                  {completedCompetitions === 0 && (
                    <div style={{
                      marginTop: '0.75rem',
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '0.85rem',
                      color: 'var(--text-gray)',
                      fontStyle: 'italic',
                      opacity: 0.7,
                    }}>
                      No competitions completed yet
                    </div>
                  )}
                </LeagueCard>
              );
            })
          )}
        </>
      ) : (
        <FormSection>
          <FormTitle>Start New Banana Cup</FormTitle>
          <form onSubmit={handleCreateLeague}>
            <FormGrid>
              <FormGroup
                style={
                  isMobile
                    ? { width: '100%', maxWidth: '100%' }
                    : { gridColumn: 'span 1', maxWidth: '300px' }
                }
              >
                <Label>Banana Cup Name</Label>
                <Input
                  type="text"
                  value={newLeagueData.name}
                  onChange={(e) => setNewLeagueData({ ...newLeagueData, name: e.target.value })}
                  placeholder="Enter Banana Cup name"
                  maxLength={25}
                  required
                  style={isMobile ? { width: '100%', maxWidth: '100%' } : { maxWidth: '280px' }}
                />
              </FormGroup>
              <FormGroup
                style={
                  isMobile
                    ? { width: '100%', maxWidth: '100%', marginLeft: 0 }
                    : { gridColumn: '3 / span 1', maxWidth: '120px', marginLeft: '2rem' }
                }
              >
                <Label style={{ whiteSpace: 'nowrap', fontSize: '0.95rem' }}>Number of Competitions</Label>
                <Input
                  type="number"
                  value={newLeagueData.numberOfCompetitions}
                  onChange={(e) => setNewLeagueData({ ...newLeagueData, numberOfCompetitions: Number(e.target.value) })}
                  min="1"
                  max="9999"
                  required
                  style={{ width: '60px', maxWidth: '60px' }}
                />
              </FormGroup>
            </FormGrid>

            <FormGroup style={{ marginTop: '1rem' }}>
              <Label>Punishment for last place (Optional)</Label>
              <InputWithIcon>
                <Input
                  type="text"
                  value={newLeagueData.punishment}
                  onChange={(e) => setNewLeagueData({ ...newLeagueData, punishment: e.target.value })}
                  placeholder="Enter punishment or click random icon"
                />
                <RandomIconButton
                  type="button"
                  onClick={handleRandomPunishment}
                  title="Random punishment"
                >
                  🎲
                </RandomIconButton>
              </InputWithIcon>
            </FormGroup>

            <FormGroup style={{ marginTop: '1rem' }}>
              <Label>Select Participating Athletes</Label>
              <AthleteGrid>
                {athletes.map(athlete => {
                  const plan = (athlete as any).subscription?.plan || 'free';
                  const canSelect = hasPlanOrHigher(plan, 'hs90');
                  return (
                    <AthleteCheckbox
                      key={athlete._id}
                      selected={newLeagueData.athleteIds.includes(athlete._id)}
                      style={{ opacity: canSelect ? 1 : 0.6 }}
                    >
                      <input
                        type="checkbox"
                        checked={newLeagueData.athleteIds.includes(athlete._id)}
                        disabled={!canSelect}
                        onChange={(e) => {
                          if (!canSelect) return;
                          if (e.target.checked) {
                            setNewLeagueData({
                              ...newLeagueData,
                              athleteIds: [...newLeagueData.athleteIds, athlete._id],
                            });
                          } else {
                            setNewLeagueData({
                              ...newLeagueData,
                              athleteIds: newLeagueData.athleteIds.filter(id => id !== athlete._id),
                            });
                          }
                        }}
                        style={{ margin: 0 }}
                      />
                      {athlete.firstName} {athlete.lastName}{!canSelect ? ' (Upgrade plan)' : ''}
                    </AthleteCheckbox>
                  );
                })}
              </AthleteGrid>
            </FormGroup>

            <div
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '0.75rem' : '1rem',
                marginTop: '1rem',
              }}
            >
              <SubmitButton type="submit">CREATE BANANA CUP 🍌</SubmitButton>
              <SecondaryButton
                type="button"
                onClick={() => {
                  setShowNewLeagueForm(false);
                  setNewLeagueData({ name: '', numberOfCompetitions: 1, athleteIds: [], punishment: '' });
                }}
              >
                Cancel
              </SecondaryButton>
            </div>
          </form>
        </FormSection>
      )}
    </Container>
  );
};

export default BananaCup;

